import React from 'react';
import { FileCheck, ChevronRight } from 'lucide-react';
import { VISA_DATA } from '@/data/essentials_b';
import { usePassport, COUNTRY_LABELS } from '@/context/PassportContext';

export default function VisaCard({ destCountryCode, destCountryName }) {
  const { origin, setOrigin } = usePassport();
  if (!destCountryCode) return null;
  const destCC = destCountryCode.toUpperCase();
  const originVisaData = VISA_DATA[origin] || {};
  const d = originVisaData[destCC];
  const sameCountry = origin === destCC;
  return (
    <div className="puvio-card p-5" data-testid="visa-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-bold text-[11px] uppercase tracking-[0.22em] text-[var(--puvio-mint-deep)]">Visa requirements</p>
          <p className="text-xs text-[var(--puvio-text-2)] mt-0.5 font-medium">For {COUNTRY_LABELS[origin] || origin} passport → {destCountryName || destCC}</p>
        </div>
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: '#E0E8FF' }}>
          <FileCheck className="w-6 h-6 text-[#0F1A13]" />
        </div>
      </div>
      <label className="mt-4 flex items-center justify-between gap-2 text-[11px] font-semibold text-[var(--puvio-text-2)] uppercase tracking-wide">
        <span>Your passport</span>
        <select
          value={origin}
          onChange={(e) => setOrigin(e.target.value)}
          className="puvio-pill puvio-pill-light text-[12px] font-bold"
          data-testid="passport-origin-select"
          aria-label="Passport origin country"
        >
          {Object.entries(COUNTRY_LABELS).map(([cc, label]) => (
            <option key={cc} value={cc}>{label}</option>
          ))}
        </select>
      </label>
      {sameCountry ? (
        <p className="mt-4 text-sm text-[var(--puvio-text-2)]">You’re in your home country — no visa needed.</p>
      ) : !d ? (
        <p className="mt-4 text-sm text-[var(--puvio-text-2)]">Visa data not yet curated for this route. Check the {COUNTRY_LABELS[origin] || 'relevant'} embassy of {destCountryName || destCC} for current requirements.</p>
      ) : (
        <>
          <div className="mt-4 rounded-2xl bg-[var(--puvio-bg-soft)] border border-[var(--puvio-border)] p-3">
            <p className="text-sm font-extrabold text-[#0F1A13]" data-testid="visa-type">{d.type}</p>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[12px]">
              <div><p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--puvio-text-3)]">Cost</p><p className="font-bold text-[#0F1A13]" data-testid="visa-cost">{d.cost}</p></div>
              <div><p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--puvio-text-3)]">Valid</p><p className="font-bold text-[#0F1A13]">{d.validity}</p></div>
              <div className="col-span-2"><p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--puvio-text-3)]">Processing</p><p className="font-bold text-[#0F1A13]">{d.processing}</p></div>
            </div>
          </div>
          <details className="mt-3" data-testid="visa-requirements">
            <summary className="cursor-pointer text-[13px] font-bold text-[var(--puvio-mint-deep)] inline-flex items-center gap-1">
              Requirements <ChevronRight className="w-3.5 h-3.5" />
            </summary>
            <ul className="mt-2 space-y-1.5">
              {d.requirements.map((r, i) => (
                <li key={i} className="text-[12px] text-[var(--puvio-text-2)] flex gap-2">
                  <span className="text-[var(--puvio-mint-deep)] font-bold">•</span><span>{r}</span>
                </li>
              ))}
            </ul>
          </details>
          {d.note && <p className="mt-3 text-[12px] text-[var(--puvio-text-2)] leading-relaxed italic">{d.note}</p>}
        </>
      )}
    </div>
  );
}
